// src/pages/AuditoriaDocFiscal.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Chip, Alert, TextField, Grid,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api from '../api/api';

const AuditoriaDocFiscal = () => {
  const [loading, setLoading] = useState(true);
  const [gruposDTE, setGruposDTE] = useState([]);

  // Filtros de fecha
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);

  const token = localStorage.getItem('token');

  const payload = useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error decodificando token:', e);
      return null;
    }
  }, [token]);

  const sucursalId = payload?.id_sucursal;
  const nombreSucursal = payload?.nombre_sucursal || 'Sucursal actual';

  useEffect(() => {
    if (!sucursalId) {
      setLoading(false);
      return;
    }

    const fetchTodos = async () => {
      setLoading(true);
      try {
        const res = await api.get('/documentos_fiscales/listado', {
          params: {
            page: 1,
            limit: 10000,
            id_sucursal: sucursalId
          }
        });

        let docs = res.data.data || [];

        // Aplicar filtro de fechas si está activo
        if (fechaInicio || fechaFin) {
          docs = docs.filter(doc => {
            const fechaDoc = dayjs(doc.fecha_emision);
            if (fechaInicio && fechaDoc.isBefore(fechaInicio, 'day')) return false;
            if (fechaFin && fechaDoc.isAfter(fechaFin, 'day')) return false;
            return true;
          });
        }

        const grupos = {};

        docs.forEach(doc => {
          const numeroControl = doc.numero_control?.trim();
          if (!numeroControl) return;

          const match = numeroControl.match(/^(.*)-(\d+)$/);
          if (match) {
            const prefijo = match[1];
            const numeroStr = match[2];
            const numero = parseInt(numeroStr, 10);

            if (isNaN(numero)) return;

            if (!grupos[prefijo]) {
              grupos[prefijo] = {
                documentos: [],
                montos: []
              };
            }
            grupos[prefijo].documentos.push(numero);
            grupos[prefijo].montos.push(parseFloat(doc.monto_total_pagar || 0));
          }
        });

        const resultados = Object.entries(grupos).map(([prefijo, data]) => {
          const numeros = data.documentos.sort((a, b) => a - b);
          const montos = data.montos;

          const min = numeros[0];
          const max = numeros[numeros.length - 1];
          const esperados = max - min + 1;
          const encontrados = numeros.length;

          const setNumeros = new Set(numeros);
          const faltantes = [];
          for (let i = min; i <= max; i++) {
            if (!setNumeros.has(i)) faltantes.push(i);
          }

          // Cálculo monetario
          const totalMonetario = montos.reduce((sum, m) => sum + m, 0);
          const subtotal = totalMonetario / 1.13; // Asumiendo IVA 13%
          const iva = totalMonetario - subtotal;

          return {
            prefijo,
            min,
            max,
            encontrados,
            esperados,
            faltantes,
            completo: faltantes.length === 0,
            totalMonetario,
            subtotal,
            iva
          };
        });

        resultados.sort((a, b) => a.prefijo.localeCompare(b.prefijo));
        setGruposDTE(resultados);
      } catch (err) {
        console.error('Error en auditoría:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTodos();
  }, [sucursalId, fechaInicio, fechaFin]);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(val || 0);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Auditoría de Correlativos y Montos - {nombreSucursal}
        </Typography>

        {/* FILTRO POR FECHA */}
        <Grid container spacing={2} sx={{ mb: 4, maxWidth: 600 }}>
          <Grid item xs={12} sm={6}>
            <DesktopDatePicker
              label="Desde"
              value={fechaInicio}
              onChange={(newValue) => setFechaInicio(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DesktopDatePicker
              label="Hasta"
              value={fechaFin}
              onChange={(newValue) => setFechaFin(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Total series/puntos de emisión encontrados: <strong>{gruposDTE.length}</strong>
            </Typography>

            {gruposDTE.length === 0 ? (
              <Alert severity="info">
                No se encontraron documentos en el rango seleccionado.
              </Alert>
            ) : (
              gruposDTE.map((grupo) => (
                <Paper key={grupo.prefijo} sx={{ p: 4, mb: 4, bgcolor: grupo.completo ? '#e8f5e9' : '#ffebee', borderRadius: 3 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    Serie: {grupo.prefijo}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1">
                        Rango secuencial: <strong>{String(grupo.min).padStart(12, '0')}</strong> → <strong>{String(grupo.max).padStart(12, '0')}</strong>
                      </Typography>
                      <Typography variant="body1">
                        Documentos emitidos: <strong>{grupo.encontrados}</strong> | Esperados: <strong>{grupo.esperados}</strong>
                      </Typography>
                      <Typography variant="h6" color={grupo.completo ? 'success.main' : 'error.main'} sx={{ mt: 2 }}>
                        <strong>
                          {grupo.completo ? '✅ CONFORME - Sin saltos' : `❌ FALTAN ${grupo.faltantes.length} correlativo(s)`}
                        </strong>
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Total monetario emitido:
                      </Typography>
                      <Typography variant="h6" color="primary">
                        Total: <strong>{formatCurrency(grupo.totalMonetario)}</strong>
                      </Typography>
                      <Typography variant="body1">
                        Subtotal: <strong>{formatCurrency(grupo.subtotal)}</strong>
                      </Typography>
                      <Typography variant="body1">
                        IVA (13%): <strong>{formatCurrency(grupo.iva)}</strong>
                      </Typography>
                    </Grid>
                  </Grid>

                  {!grupo.completo && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" color="error" gutterBottom>
                        Números secuenciales faltantes:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {grupo.faltantes.map(num => (
                          <Chip key={num} label={String(num).padStart(12, '0')} color="error" size="small" />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))
            )}
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default AuditoriaDocFiscal;